export type LocalCategoryRow = {
  account_id?: string;
  run_id?: string;
  local_prompt_category: string;
  organic_result_count: number | null;
  organic_business_mentions: number | null;
  organic_visibility_rate: string | number | null;
  avg_prompt_score: string | number | null;
  weak_signal_count: number | null;
  error_count: number | null;
};

export type IntentRadarMetric = {
  axisKey: string;
  axisLabel: string;
  value: number | null;
  numerator: number | null;
  denominator: number | null;
  recommendationStrength: number | null;
  lowStrengthCount: number | null;
  errorCount: number | null;
};

export type IntentRadarSeries = {
  id: string;
  label: string;
  kind: "customer" | "competitor";
  metrics: IntentRadarMetric[];
};

export type GovernedLocalEntityRole = {
  account_id: string;
  canonical_entity_key: string | null;
  display_name: string | null;
  normalized_entity_name: string | null;
  raw_entity_name: string | null;
  entity_role: string | null;
  score_treatment: string | null;
  visibility_treatment: string | null;
  is_active: boolean | null;
  review_status: string | null;
  approved_at: string | null;
  source?: string | null;
};

export type GovernedLocalEntityMention = {
  account_id: string;
  run_id: string;
  local_prompt_category: string;
  prompt_result_id: string;
  entity_name: string | null;
  is_business_entity: boolean | null;
  is_comparison_prompt: boolean | null;
  visibility_signal: string | null;
};

const CATEGORY_LABELS: Record<string, string> = {
  local_recommendation: "Local Recommendations",
  local_service_specific: "Category / Service Match",
  local_trust_review: "Trust & Reviews",
  local_emergency: "High-Intent Need",
  local_problem_solution: "Research / Need-State",
  local_cost_value: "Price / Value",
  project_recommendation: "Project Recommendations",
  project_service_match: "Service Match",
  project_quality_craftsmanship: "Quality / Craftsmanship",
  project_before_hiring: "Before Hiring",
  project_price_value: "Price / Value",
  project_trust_review: "Trust & Reviews",
  dealer_recommendation: "Dealer Recommendation",
  dealer_inventory: "Dealer Inventory",
  dealer_used_cpo: "Dealer Used / CPO",
  dealer_financing_tradein: "Dealer Financing / Trade-In",
  dealer_service_center: "Dealer Service Center",
  dealer_reputation: "Dealer Reputation",
};

const CATEGORY_ORDER = [
  "local_recommendation", "local_service_specific", "local_trust_review", "local_emergency", "local_problem_solution", "local_cost_value",
  "project_recommendation", "project_service_match", "project_quality_craftsmanship", "project_before_hiring", "project_price_value", "project_trust_review",
  "dealer_recommendation", "dealer_inventory", "dealer_used_cpo", "dealer_financing_tradein", "dealer_service_center", "dealer_reputation",
];
const CATEGORY_RANK = new Map(CATEGORY_ORDER.map((category, index) => [category, index]));

export function localCategoryLabel(category: string): string {
  return (
    CATEGORY_LABELS[category] ||
    category
      .replace(/^(local|project|dealer)_/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

function finiteNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeEntityKey(value: string | null | undefined): string {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function roleEntityKey(role: GovernedLocalEntityRole): string {
  return String(role.canonical_entity_key || normalizeEntityKey(role.normalized_entity_name) || normalizeEntityKey(role.raw_entity_name) || "").trim();
}

// Match the established entity-governance contract for historical human review
// records whose legacy rows predate approved_at. Automated sources do not get
// this exception; they still need explicit approval metadata.
const HISTORICAL_HUMAN_APPROVAL_SOURCES = new Set([
  "manual_admin",
  "manual_admin_fix",
  "human_admin",
]);

function isEligibleCompetitorRole(role: GovernedLocalEntityRole): boolean {
  return role.is_active === true &&
    role.review_status === "approved" &&
    (Boolean(role.approved_at) || HISTORICAL_HUMAN_APPROVAL_SOURCES.has(String(role.source || ""))) &&
    (role.entity_role === "direct_competitor" || role.entity_role === "adjacent_competitor") &&
    (role.score_treatment === "include_primary" || role.score_treatment === "include_secondary") &&
    role.visibility_treatment !== "hide_everywhere";
}

/**
 * Maps only the currently selected account/run's governed mention evidence to
 * explicitly approved competitor identities. The SQL view currently exposes
 * governed display names but not canonical keys or review status, so its rows
 * are intersected with the account-scoped, RLS-protected role records here.
 */
export function toLocalCompetitorRadarSeries(
  customerSeries: IntentRadarSeries | null,
  categoryRows: LocalCategoryRow[],
  mentionRows: GovernedLocalEntityMention[],
  roleRows: GovernedLocalEntityRole[],
  scope: { accountId: string; runId: string }
): IntentRadarSeries[] {
  if (!customerSeries || !scope.accountId || !scope.runId) return [];

  const scopedRoles = roleRows.filter((role) => role.account_id === scope.accountId && role.is_active === true);
  const rolesByIdentity = new Map<string, GovernedLocalEntityRole[]>();
  for (const role of scopedRoles) {
    const key = roleEntityKey(role);
    if (!key) continue;
    rolesByIdentity.set(key, [...(rolesByIdentity.get(key) || []), role]);
  }

  const identityForAlias = new Map<string, Set<string>>();
  for (const [identity, roles] of rolesByIdentity) {
    for (const role of roles) {
      for (const alias of [role.canonical_entity_key, role.display_name, role.normalized_entity_name, role.raw_entity_name]) {
        const normalized = normalizeEntityKey(alias);
        if (!normalized) continue;
        const identities = identityForAlias.get(normalized) || new Set<string>();
        identities.add(identity);
        identityForAlias.set(normalized, identities);
      }
    }
  }
  const ambiguousIdentities = new Set<string>();
  for (const identities of identityForAlias.values()) {
    if (identities.size > 1) identities.forEach((identity) => ambiguousIdentities.add(identity));
  }

  const eligibleIdentities = new Map<string, { label: string; roles: GovernedLocalEntityRole[] }>();
  for (const [identity, roles] of rolesByIdentity) {
    // A conflicting active classification on the same canonical entity makes
    // that identity ambiguous; fail closed instead of choosing a favorable row.
    if (ambiguousIdentities.has(identity) || !roles.every(isEligibleCompetitorRole)) continue;
    const labels = roles
      .map((role) => String(role.display_name || role.normalized_entity_name || role.raw_entity_name || "").trim())
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right));
    if (labels.length) eligibleIdentities.set(identity, { label: labels[0], roles });
  }

  const categoryRowsByKey = new Map<string, LocalCategoryRow>();
  for (const row of categoryRows) {
    if (row.account_id !== scope.accountId || row.run_id !== scope.runId) continue;
    if (["local_comparison", "dealer_comparison"].includes(row.local_prompt_category)) continue;
    categoryRowsByKey.set(row.local_prompt_category, row);
  }
  const axisKeys = new Set(customerSeries.metrics.map((metric) => metric.axisKey));
  const responseIdsByIdentity = new Map<string, Map<string, Set<string>>>();

  for (const mention of mentionRows) {
    if (mention.account_id !== scope.accountId || mention.run_id !== scope.runId) continue;
    if (mention.is_business_entity !== false || mention.is_comparison_prompt !== false) continue;
    if (mention.visibility_signal === "error" || !axisKeys.has(mention.local_prompt_category)) continue;
    if (!categoryRowsByKey.has(mention.local_prompt_category)) continue;

    const alias = normalizeEntityKey(mention.entity_name);
    const identities = identityForAlias.get(alias);
    if (!identities || identities.size !== 1) continue;
    const identity = Array.from(identities)[0];
    if (!eligibleIdentities.has(identity) || !mention.prompt_result_id) continue;

    const byCategory = responseIdsByIdentity.get(identity) || new Map<string, Set<string>>();
    const responseIds = byCategory.get(mention.local_prompt_category) || new Set<string>();
    responseIds.add(mention.prompt_result_id);
    byCategory.set(mention.local_prompt_category, responseIds);
    responseIdsByIdentity.set(identity, byCategory);
  }

  const ranked = Array.from(eligibleIdentities.entries())
    .map(([identity, eligible]) => {
      const byCategory = responseIdsByIdentity.get(identity) || new Map<string, Set<string>>();
      return {
        identity,
        ...eligible,
        totalResponses: Array.from(byCategory.values()).reduce((total, ids) => total + ids.size, 0),
        byCategory,
      };
    })
    .sort((left, right) => right.totalResponses - left.totalResponses ||
      Number(!left.roles.some((role) => role.score_treatment === "include_primary")) - Number(!right.roles.some((role) => role.score_treatment === "include_primary")) ||
      left.label.localeCompare(right.label) || left.identity.localeCompare(right.identity))
    .slice(0, 3);

  return ranked.map((competitor) => ({
    id: `competitor:${competitor.identity}`,
    label: competitor.label,
    kind: "competitor",
    metrics: customerSeries.metrics.map((customerMetric) => {
      const numerator = competitor.byCategory.get(customerMetric.axisKey)?.size || 0;
      const category = categoryRowsByKey.get(customerMetric.axisKey);
      const denominator = finiteNumber(category?.organic_result_count);
      return {
        axisKey: customerMetric.axisKey,
        axisLabel: customerMetric.axisLabel,
        value: denominator !== null && denominator > 0 ? (numerator / denominator) * 100 : null,
        numerator: denominator === null ? null : numerator,
        denominator,
        recommendationStrength: null,
        lowStrengthCount: null,
        errorCount: null,
      };
    }),
  }));
}

export function toLocalCustomerRadarSeries(
  rows: LocalCategoryRow[],
  scope?: { accountId?: string | null; runId?: string | null }
): IntentRadarSeries | null {
  const metrics = rows
    .filter((row) => !["local_comparison", "dealer_comparison"].includes(row.local_prompt_category))
    .filter((row) => !scope?.accountId || row.account_id === scope.accountId)
    .filter((row) => !scope?.runId || row.run_id === scope.runId)
    .map((row) => ({
      axisKey: row.local_prompt_category,
      axisLabel: localCategoryLabel(row.local_prompt_category),
      value: finiteNumber(row.organic_visibility_rate),
      numerator: finiteNumber(row.organic_business_mentions),
      denominator: finiteNumber(row.organic_result_count),
      recommendationStrength: finiteNumber(row.avg_prompt_score),
      lowStrengthCount: finiteNumber(row.weak_signal_count) ?? 0,
      errorCount: finiteNumber(row.error_count) ?? 0,
    }))
    .sort((left, right) =>
      (CATEGORY_RANK.get(left.axisKey) ?? CATEGORY_ORDER.length) -
      (CATEGORY_RANK.get(right.axisKey) ?? CATEGORY_ORDER.length)
    );

  if (metrics.length === 0) return null;
  return { id: "customer", label: "Your business", kind: "customer", metrics };
}
